import { Component, OnInit, OnDestroy, input, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormGroup, ReactiveFormsModule, Validators, FormControl, FormsModule } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { ClassService } from '@fitness-force/api';
import { Store } from '@ngrx/store';
import { TENANTS_INFO, TRIAL_MASTER_STATE } from '@fitness-force/state';
import { TrialService } from '@fitness-force/api';
import { Status, TrialType } from '@fitness-force/enum';
import { TimeconversionService } from '@fitness-force/api';
import { AssignedToComponent } from '@fitness-force/common/assigned-to';
import { ErrorMsgComponent } from '@fitness-force/errors';;
import type { TrialMaster } from '@fitness-force/models';

@Component({
  selector: 'walk-in-trial',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DatePickerModule,
    SelectModule,
    ButtonModule,
    RippleModule,
    TranslateModule,
    AssignedToComponent,
    ErrorMsgComponent
],
  templateUrl: './walk-in-trial.component.html'
})
export class WalkInTrialComponent implements OnInit, OnDestroy {
  private classService = inject(ClassService);
  private datepipe = inject(DatePipe);
  private store = inject(Store);
  private trialMasterService = inject(TrialService);
  private timeService = inject(TimeconversionService);

  readonly parentForm = input.required<FormGroup>();
  readonly staffList = input<any[]>([]);
  readonly classList = input<any[]>([]);
  readonly formType = input<string>('walk-in-trial');

  localClassList: any[] = [];

  trial1 = 0; // 0 for Membership, 1 for Class
  private destroy$ = new Subject<void>();
  minDate: Date = new Date();
  tenantId: number = 0;
  trialMaster: TrialMaster;
  maxTrialAllowed: number = 0;
  selectedTrials: any[] = [];
  trials: any[] = []; // your trials data
  trialCount: number = 1;

  ngOnInit() {
    this.store.select(TENANTS_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe(tenant => {
        this.tenantId = tenant.selected_tenant.id;
        this.getTrialMaster();
      });

    // Check if trial_membership form group exists, if not create it
    const parentForm = this.parentForm();
    if (!parentForm.contains('trial_membership')) {
      parentForm.addControl('trial_membership', new FormGroup({
        trial_start_date: new FormControl(null, [this.trialMaster && (this.trialMaster.type === TrialType.DaysBased || this.trialMaster.type.toString() === TrialType[TrialType.DaysBased]) ? Validators.required : Validators.nullValidator]),
        notes: new FormControl('', [this.trialMaster && (this.trialMaster.type === TrialType.DaysBased || this.trialMaster.type.toString() === TrialType[TrialType.DaysBased]) ? Validators.required : Validators.nullValidator]),
        trial_preferred_time: new FormControl('', [this.trialMaster && (this.trialMaster.type === TrialType.DaysBased || this.trialMaster.type.toString() === TrialType[TrialType.DaysBased]) ? Validators.required : Validators.nullValidator]),
        trial_owner_staff_id: new FormControl(0, [Validators.required])
      }));
    }

    // Check if trial_classes control exists, if not create it
    if (!parentForm.contains('trial_classes')) {
      parentForm.addControl('trial_classes', new FormControl([]));
    }

    if(this.trialMaster && (this.trialMaster.type === TrialType.DaysBased || this.trialMaster.type.toString() === TrialType[TrialType.DaysBased])) {  
      parentForm.addControl('selected_trial_type', new FormControl('membership'));
    } else if(this.trialMaster && (this.trialMaster.type === TrialType.ClassBased || this.trialMaster.type.toString() === TrialType[TrialType.ClassBased])) {
      parentForm.addControl('selected_trial_type', new FormControl('class'));
    }
    else {
      parentForm.addControl('selected_trial_type', new FormControl(null));
    }

    if(this.trialMaster && (this.trialMaster.type === TrialType.DaysBased || this.trialMaster.type.toString() === TrialType[TrialType.DaysBased])) {  
      this.parentForm().get('trial_classes')?.setValue([]);
      this.parentForm().get('trial_classes')?.clearValidators();
      this.parentForm().get('trial_classes')?.updateValueAndValidity({ emitEvent: false });
    }
    else if(this.trialMaster && (this.trialMaster.type === TrialType.ClassBased || this.trialMaster.type.toString() === TrialType[TrialType.ClassBased])) {
      this.parentForm().get('trial_membership')?.patchValue({
        trial_start_date: null,
        notes: '',
        trial_preferred_time: '',
        trial_owner_staff_id: 0
      });
      const trialMembershipGroup = parentForm.get('trial_membership') as FormGroup;
      if (trialMembershipGroup) {
        Object.keys(trialMembershipGroup.controls).forEach(key => {
          const control = trialMembershipGroup.get(key);
          if (control) {
            control.clearValidators();
            control.removeValidators(Validators.required);
            control.updateValueAndValidity({ emitEvent: false });
          }
        });
      }
      this.parentForm().get('trial_membership')?.clearValidators();
      this.parentForm().get('trial_membership')?.updateValueAndValidity({ emitEvent: true });
    }
    parentForm.updateValueAndValidity();

    // Initialize trial selections form group
    if (!parentForm.contains('trial_selections')) {
      const trialSelectionsGroup = new FormGroup({});
      for (let i = 0; i < this.maxTrialAllowed; i++) {
        trialSelectionsGroup.addControl(`trial_${i}`, new FormControl(null));
      }
      parentForm.addControl('trial_selections', trialSelectionsGroup);
    }

    // Subscribe to createWalkInTrial changes
    // this.parentForm.get('createWalkInTrial')?.valueChanges
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe(createTrial => {
    //     if (createTrial) {
    //       this.updateValidation();
    //     } else {
    //       // Clear validations when trial is disabled
    //       this.clearAllValidations();
    //     }
    //   });

    // // Initial validation setup
    // if (this.parentForm.get('createWalkInTrial')?.value) {
    //   this.updateValidation();
    // }
    this.selectedTrials = new Array(this.maxTrialAllowed).fill(null);
  }

  getTrialMaster() {
    this.store.select(TRIAL_MASTER_STATE).pipe(takeUntil(this.destroy$)).subscribe((trialMasters: TrialMaster[]) => {
      if (trialMasters && trialMasters.length > 0) {
        this.trialMaster = trialMasters.find((o: any) => o.status == Status.Active || o.status.toString() == Status[Status.Active])!;
        this.maxTrialAllowed = this.trialMaster.max_trial_class || 0;
        if(this.trialMaster)
        {
          if(this.trialMaster.type === TrialType.ClassBased || this.trialMaster.type.toString() === TrialType[TrialType.ClassBased]) {
            this.getFutureClasses();
          }
        }
      }
    });
  }

  onTrialSelect(event: any, index: number) {
    // Update the trial_classes array with all selected values
    const selections = this.parentForm().get('trial_selections') as FormGroup;
    const selectedClasses = Object.values(selections.value)
      .filter(value => value) // Remove null values
      .map((trial: any) => trial.id); // Get class IDs

    this.parentForm().get('trial_classes')?.setValue(selectedClasses);
  }

  private clearAllValidations() {
    const trialMembershipGroup = this.parentForm().get('trial_membership') as FormGroup;
    if (trialMembershipGroup) {
      Object.keys(trialMembershipGroup.controls).forEach(key => {
        const control = trialMembershipGroup.get(key);
        if (control) {
          control.clearValidators();
          control.updateValueAndValidity({ emitEvent: false });
        }
      });
    }

    const trialClassesControl = this.parentForm().get('trial_classes');
    if (trialClassesControl) {
      trialClassesControl.clearValidators();
      trialClassesControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  onTrialTypeChange(type: number) {
    this.trial1 = type;
    if (type === 0) {
      this.parentForm().get('trial_classes')?.setValue([]);
      this.parentForm().get('trial_classes')?.clearValidators();
      this.parentForm().get('trial_classes')?.updateValueAndValidity({ emitEvent: false });
      this.parentForm().get('selected_trial_type')?.setValue('membership');
      this.trialCount = 1;
    } else {
      this.parentForm().get('trial_membership')?.patchValue({
        trial_start_date: null,
        notes: '',
        trial_preferred_time: '',
        trial_owner_staff_id: 0
      });
      this.parentForm().get('trial_membership')?.clearValidators();
      this.parentForm().get('trial_membership')?.updateValueAndValidity({ emitEvent: false });
      this.parentForm().get('selected_trial_type')?.setValue('class');
      this.trialCount = 1;
    }
    this.updateValidation();
  }

  private updateValidation() {
    if (this.trial1 === 0) {
      // Membership trial validation
      const trialMembershipGroup = this.parentForm().get('trial_membership') as FormGroup;
      if (trialMembershipGroup) {
        Object.keys(trialMembershipGroup.controls).forEach(key => {
          if (key !== 'notes') { // Don't make notes required
            const control = trialMembershipGroup.get(key);
            if (control) {
              control.setValidators([Validators.required]);
              control.updateValueAndValidity({ emitEvent: false });
            }
          }
        });
      }
      // Clear class validation
      this.parentForm().get('trial_classes')?.clearValidators();
      this.parentForm().get('trial_classes')?.updateValueAndValidity({ emitEvent: false });
    } else {
      // Class trial validation
      const trialClassesControl = this.parentForm().get('trial_classes');
      if (trialClassesControl) {
        trialClassesControl.setValidators([Validators.required]);
        trialClassesControl.updateValueAndValidity({ emitEvent: false });
      }
      // Clear membership validation
      const trialMembershipGroup = this.parentForm().get('trial_membership') as FormGroup;
      if (trialMembershipGroup) {
        Object.keys(trialMembershipGroup.controls).forEach(key => {
          const control = trialMembershipGroup.get(key);
          if (control) {
            control.clearValidators();
            control.updateValueAndValidity({ emitEvent: false });
          }
        });
      }
    }
  }

  getTrialSelections(): number[] {
    return Array(this.maxTrialAllowed).fill(0).map((_, i) => i);
  }

  getFutureClasses() {
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 7);
    const getClassesRequest: any = {
      tenant_id: this.tenantId,
      start_date: this.datepipe.transform(startDate.toISOString(), 'yyyy-MM-dd')!,
      end_date: this.datepipe.transform(endDate.toISOString(), 'yyyy-MM-dd')!,
      getClientDetails: false,
    };
    this.classService.GetClasses(getClassesRequest).subscribe((classes) => {
      var data = classes.items ? classes.items.filter((o: any) => o.accepts_trial == true) : [];
      this.localClassList = data.map((o: any) => ({
        id: o.id,
        name: o.type_name_primary_language || o.type_name_secondary_language,
        desc: this.getFromatedDates(o)
      }));
    });
  }

  getFromatedDates(o: any) {
    var startDate = this.timeService.ConvertUTCToLocalDateTime(o.start_datetime);
    var endDate = this.timeService.ConvertUTCToLocalDateTime(o.end_datetime);
    var formattedStartDate = this.datepipe.transform(startDate, 'MM/dd/yyyy hh:mm a');
    var formattedEndDate = this.datepipe.transform(endDate, 'MM/dd/yyyy hh:mm a');
    return formattedStartDate + ' - ' + formattedEndDate;
  }

 

  onAddTrial() {
    if (this.trialCount < this.maxTrialAllowed) {
      this.trialCount++;
    }
  }

  get trialOwnerFormControl() {
    return this.parentForm().get('trial_membership')?.get('trial_owner_staff_id') as FormControl;
  }

  onTrainerChange(event: any) {
    this.trialOwnerFormControl.setValue(event.id);
  }

  isDaysBasedTrial() {
    return this.trialMaster.type === TrialType.DaysBased || this.trialMaster.type.toString() === TrialType[TrialType.DaysBased];
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
} 